/**
 * Search modal.
 *
 * Two sources, deliberately different. Colours are already in the document,
 * so they filter as fast as the customer types with no request at all.
 * Products and editorial come from Shopify's predictive search endpoint,
 * rendered by sections/predictive-search.liquid so prices and translations
 * stay correct per market.
 *
 * The form posts to /search without any of this, so a failed module or a slow
 * network degrades to an ordinary search rather than to a broken field.
 */
import { animate, prefersReducedMotion, trapFocus } from 'theme:motion';

const modal = document.querySelector('[data-search-modal]');
if (modal) {
  const dialog = modal.querySelector('[data-search-dialog]');
  const input = modal.querySelector('[data-search-input]');
  const idle = modal.querySelector('[data-search-idle]');
  const results = modal.querySelector('[data-search-results]');
  const remote = modal.querySelector('[data-search-remote]');
  const empty = modal.querySelector('[data-search-empty]');
  const colourSection = modal.querySelector('[data-search-colour-results]');
  const colourList = modal.querySelector('[data-search-colour-list]');
  const announce = modal.querySelector('[data-search-announce]');

  const colourItems = [...modal.querySelectorAll('[data-colour-item]')];

  const DEBOUNCE_MS = 180;
  const MIN_QUERY = 2;
  const MAX_COLOURS = 12;

  let debounceTimer = null;
  let releaseFocus = null;
  let controller = null;
  let activeIndex = -1;

  /* ---------------------------------------------------------------------
     Open and close
     --------------------------------------------------------------------- */

  function open(trigger) {
    if (!dialog.open) dialog.showModal();
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    trigger?.setAttribute('aria-expanded', 'true');
    releaseFocus = trapFocus(dialog);

    // Focus after the panel has started moving, so iOS does not scroll the
    // page to chase the field while it is still off screen.
    requestAnimationFrame(() => input?.focus());

    if (prefersReducedMotion()) return;
    animate(
      idle?.children ?? [],
      { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0px)'] },
      { duration: 0.45, delay: (index) => 0.05 + index * 0.05, easing: [0.16, 1, 0.3, 1] }
    );
  }

  function close() {
    modal.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.querySelector('[data-search-trigger]')?.setAttribute('aria-expanded', 'false');

    const finish = () => {
      if (dialog.open) dialog.close();
      releaseFocus?.();
      releaseFocus = null;
    };

    prefersReducedMotion() ? finish() : setTimeout(finish, 420);
  }

  /* ---------------------------------------------------------------------
     Querying
     --------------------------------------------------------------------- */

  function renderColours(query) {
    if (!colourList || !colourSection) return 0;

    const matches = colourItems
      .filter((item) => {
        const name = item.dataset.colourName ?? '';
        const family = item.dataset.colourFamily ?? '';
        return name.includes(query) || family.includes(query);
      })
      .slice(0, MAX_COLOURS);

    colourList.replaceChildren(...matches.map((item) => item.cloneNode(true)));
    colourSection.hidden = matches.length === 0;
    return matches.length;
  }

  async function fetchRemote(query) {
    controller?.abort();
    controller = new AbortController();

    const url = new URL(`${window.Shopify.routes.root}search/suggest`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,page,article');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', 'predictive-search');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Predictive search unavailable');

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const payload = parsed.querySelector('[data-search-remote-count]');

      remote.replaceChildren(...(payload ? [...payload.childNodes] : []));
      return Number(payload?.dataset.searchRemoteCount ?? 0);
    } catch (error) {
      if (error.name === 'AbortError') return null;
      // A failed suggestion request is not a failure of the feature: the form
      // still submits to the full results page.
      remote.replaceChildren();
      return 0;
    }
  }

  async function run(rawQuery) {
    const query = rawQuery.trim().toLowerCase();
    activeIndex = -1;

    if (query.length < MIN_QUERY) {
      results.hidden = true;
      idle.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      remote.replaceChildren();
      return;
    }

    idle.hidden = true;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');

    const colourCount = renderColours(query);
    const remoteCount = await fetchRemote(query);
    if (remoteCount === null) return; // superseded by a newer keystroke

    const total = colourCount + remoteCount;
    empty.hidden = total > 0;

    if (announce) {
      announce.textContent = total
        ? `${total}`
        : (empty.querySelector('h2')?.textContent ?? '');
    }
  }

  input?.addEventListener('input', (event) => {
    clearTimeout(debounceTimer);
    const value = event.target.value;
    debounceTimer = setTimeout(() => run(value), DEBOUNCE_MS);
  });

  /* ---------------------------------------------------------------------
     Keyboard

     A roving highlight over whatever is currently on screen, so the caret can
     stay in the field while the customer arrows through results.
     --------------------------------------------------------------------- */

  function focusables() {
    const scope = results.hidden ? idle : results;
    return [...scope.querySelectorAll('a[href]')].filter((el) => el.offsetParent !== null);
  }

  function moveActive(delta) {
    const items = focusables();
    if (!items.length) return;

    items.forEach((item) => item.removeAttribute('data-search-active'));
    activeIndex = (activeIndex + delta + items.length) % items.length;
    const next = items[activeIndex];
    next.setAttribute('data-search-active', '');
    next.scrollIntoView({ block: 'nearest' });
  }

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter' && activeIndex > -1) {
      const items = focusables();
      if (items[activeIndex]) {
        event.preventDefault();
        items[activeIndex].click();
      }
    }
  });

  dialog?.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  /* ---------------------------------------------------------------------
     Triggers
     --------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-search-trigger]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }
    if (event.target.closest('[data-search-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    const typingElsewhere =
      event.target.matches('input, textarea, select') || event.target.isContentEditable;

    const slash = event.key === '/' && !typingElsewhere;
    const commandK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);

    if (slash || commandK) {
      event.preventDefault();
      open(document.querySelector('[data-search-trigger]'));
    }
  });
}
